import { useId, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { format, parseISO } from 'date-fns';
import { AlertTriangleIcon, CheckIcon, XIcon } from 'lucide-react';
import { type Control, Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type AnimalDto,
  type AnimalRow,
  type AnimalTypeLabel,
  buildConvertInput,
  CONVERT_FIELDS,
  type ConvertField,
  type ConvertTargetType,
  type ConvertValues,
  mapAnimalDto,
  toConvertValues,
  useConvertAnimalType,
} from '@/services/animal-management';
import { OPTION_SET_OBJECT_TYPES } from '@/services/animal-management/api';
import { useToast } from '@/services/toast';

import {
  OptionSelect,
  useAnimalStatusOptions,
  useKeyedStringMapOptions,
} from './AnimalFieldInputs';
import { LookupCombobox } from './LookupCombobox';

const CONVERSION_RULES: Record<AnimalTypeLabel, AnimalTypeLabel[]> = {
  Calf: [],
  Donor: ['Recipient'],
  Recipient: ['Donor'],
  'LOPU Donor': ['Donor', 'Recipient'],
};

const isConvertTarget = (type: AnimalTypeLabel): type is ConvertTargetType =>
  type === 'Donor' || type === 'Recipient';

export const canConvertType = (type: AnimalTypeLabel) =>
  CONVERSION_RULES[type].some(isConvertTarget);

function isIrreversibleConversion(
  current: AnimalTypeLabel,
  target: AnimalTypeLabel,
): boolean {
  return !CONVERSION_RULES[target].includes(current);
}

const FIELD_LABELS: Record<ConvertField, { key: string; label: string }> = {
  receivedDate: {
    key: 'animalManagement.columns.receivedDate',
    label: 'Received Date',
  },
  recipientStatusId: {
    key: 'animalManagement.columns.recipientStatus',
    label: 'Recip Status',
  },
  ocvStatusId: {
    key: 'animalManagement.columns.ocvStatus',
    label: 'OCV Status',
  },
  client: { key: 'animalManagement.columns.client', label: 'Original Owner' },
  colorId: { key: 'animalManagement.columns.color', label: 'Color' },
};

const convertValuesSchema = z.object({
  receivedDate: z.string(),
  recipientStatusId: z.string(),
  ocvStatusId: z.string(),
  client: z.string(),
  colorId: z.string(),
}) satisfies z.ZodType<ConvertValues>;

const convertSchema = convertValuesSchema
  .extend({ newType: z.string() })
  .superRefine((data, ctx) => {
    const target = data.newType as AnimalTypeLabel;
    if (!isConvertTarget(target)) return;
    for (const field of CONVERT_FIELDS[target]) {
      if (!data[field].trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: [field],
        });
      }
    }
  });

type ConvertFormValues = z.infer<typeof convertSchema>;

type ConvertTypeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  animal: AnimalRow;
  onConverted: (animal: AnimalRow) => void;
};

function NewTypeField({
  field,
  target,
  control,
  animal,
}: {
  field: ConvertField;
  target: ConvertTargetType;
  control: Control<ConvertFormValues>;
  animal: AnimalRow;
}) {
  const { t } = useTranslation();
  const id = useId();
  const isRecipient = target === 'Recipient';
  const recipientStatuses = useAnimalStatusOptions(
    'recipient',
    isRecipient && field === 'recipientStatusId',
  );
  const ocvStatuses = useAnimalStatusOptions(
    'ocv',
    isRecipient && field === 'ocvStatusId',
  );
  const clients = useKeyedStringMapOptions(
    OPTION_SET_OBJECT_TYPES.recipient,
    'tog_client',
    isRecipient && field === 'client',
  );
  const { key, label } = FIELD_LABELS[field];

  return (
    <Controller
      name={field}
      control={control}
      render={({ field: formField, fieldState }) => {
        const common = {
          id,
          value: formField.value,
          onChange: formField.onChange,
          invalid: fieldState.invalid,
        };
        let input: React.ReactNode;
        switch (field) {
          case 'receivedDate':
            input = (
              <DatePicker
                id={id}
                className="h-8"
                value={formField.value ? parseISO(formField.value) : undefined}
                onChange={(date) =>
                  formField.onChange(date ? format(date, 'yyyy-MM-dd') : '')
                }
              />
            );
            break;
          case 'recipientStatusId':
            input = (
              <OptionSelect
                {...common}
                options={recipientStatuses.options}
                selectedLabel={animal.recipientStatus}
                disabled={recipientStatuses.isLoading}
              />
            );
            break;
          case 'ocvStatusId':
            input = (
              <OptionSelect
                {...common}
                options={ocvStatuses.options}
                selectedLabel={animal.ocvStatus}
                disabled={ocvStatuses.isLoading}
              />
            );
            break;
          case 'client':
            input = (
              <OptionSelect
                {...common}
                options={clients.options}
                disabled={clients.isLoading}
              />
            );
            break;
          case 'colorId':
            input = (
              <LookupCombobox
                id={id}
                kind="color"
                value={formField.value || null}
                onChange={(next) => formField.onChange(next ?? '')}
                selectedLabel={animal.color}
                invalid={fieldState.invalid}
              />
            );
            break;
        }
        return (
          <Field data-invalid={fieldState.invalid}>
            <FieldLabel htmlFor={id}>{t(key, label)}*</FieldLabel>
            {input}
            {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
          </Field>
        );
      }}
    />
  );
}

export function ConvertTypeDialog({
  open,
  onOpenChange,
  animal,
  onConverted,
}: ConvertTypeDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const formId = useId();
  const convert = useConvertAnimalType();
  const [pendingConversion, setPendingConversion] =
    useState<ConvertFormValues | null>(null);

  const allowedTargets = CONVERSION_RULES[animal.animalType];
  const defaultValues = useMemo<ConvertFormValues>(
    () => ({ newType: animal.animalType, ...toConvertValues(animal) }),
    [animal],
  );

  const form = useForm<ConvertFormValues>({
    resolver: zodResolver(convertSchema),
    values: open ? defaultValues : undefined,
  });

  const watchedNewType = form.watch('newType') as AnimalTypeLabel;
  const target =
    watchedNewType !== animal.animalType && isConvertTarget(watchedNewType)
      ? watchedNewType
      : null;
  const irreversible =
    target !== null && isIrreversibleConversion(animal.animalType, target);

  const close = () => {
    setPendingConversion(null);
    onOpenChange(false);
  };

  const runConversion = async (data: ConvertFormValues) => {
    const newType = data.newType as ConvertTargetType;
    const result = await convert.mutateAsync({
      id: animal.id,
      input: buildConvertInput(newType, data),
    });
    if (!result.success) {
      setPendingConversion(null);
      toast.show({
        severity: 'error',
        summary: t('common.error', 'Error'),
        detail:
          result.message ??
          t(
            'animalManagement.convertAnimalType.failed',
            'Failed to convert animal',
          ),
        life: 8000,
      });
      return;
    }
    const converted = mapAnimalDto(result.data as AnimalDto);
    toast.show({
      severity: 'success',
      summary: t('common.success', 'Success'),
      detail: t('animalManagement.convertAnimalType.converted', {
        defaultValue: '{nameTag} converted to {type}.',
        nameTag: animal.nameTag,
        type: newType,
      }),
    });
    close();
    onConverted(converted);
  };

  const handleFormSubmit = (data: ConvertFormValues) => {
    if (irreversible) {
      setPendingConversion(data);
      return;
    }
    return runConversion(data);
  };

  return (
    <>
      <Dialog open={open && !pendingConversion} onOpenChange={close} modal>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t(
                'animalManagement.convertAnimalType.title',
                'Convert Animal Type',
              )}
            </DialogTitle>
            <DialogDescription>
              {t('animalManagement.convertAnimalType.subtitle', {
                defaultValue:
                  'Select the new type for {nameTag} and fill in required fields',
                nameTag: animal.nameTag,
              })}
            </DialogDescription>
          </DialogHeader>

          <DialogBody
            as="form"
            id={formId}
            onSubmit={form.handleSubmit(handleFormSubmit)}
            className="flex flex-col gap-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <Field>
                <FieldLabel>
                  {t(
                    'animalManagement.convertAnimalType.currentType',
                    'Current Type:',
                  )}
                </FieldLabel>
                <Input value={animal.animalType} disabled />
              </Field>

              <Controller
                name="newType"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel>
                      {t(
                        'animalManagement.convertAnimalType.newType',
                        'New Type:',
                      )}
                    </FieldLabel>
                    <Select {...field}>
                      <SelectTrigger
                        aria-invalid={fieldState.invalid}
                        className="w-full"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={animal.animalType}>
                          {animal.animalType} (Current)
                        </SelectItem>
                        {allowedTargets.map((type) => (
                          <SelectItem
                            key={type}
                            value={type}
                            disabled={!isConvertTarget(type)}
                          >
                            {isConvertTarget(type)
                              ? type
                              : t(
                                  'animalManagement.convertAnimalType.notAvailable',
                                  {
                                    defaultValue: '{type} (not available yet)',
                                    type,
                                  },
                                )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />
            </div>

            {irreversible && (
              <div className="flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
                <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
                <span>
                  {t(
                    'animalManagement.convertAnimalType.irreversibleWarning',
                    'This conversion requires confirmation and cannot be undone.',
                  )}
                </span>
              </div>
            )}

            {target && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="text-sm font-semibold">
                  {t(
                    'animalManagement.convertAnimalType.typeSpecificFieldsTitle',
                    {
                      defaultValue: '{type} Specific Fields',
                      type: target,
                    },
                  )}
                </div>
                <p className="text-muted-foreground mb-3 text-sm">
                  {t(
                    'animalManagement.convertAnimalType.typeSpecificFieldsHelper',
                    'Fill in the required fields for the new animal type before converting.',
                  )}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {CONVERT_FIELDS[target].map((field) => (
                    <NewTypeField
                      key={`${target}-${field}`}
                      field={field}
                      target={target}
                      control={form.control}
                      animal={animal}
                    />
                  ))}
                </div>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              <XIcon className="mr-2 size-4" />
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              form={formId}
              disabled={!target || convert.isPending}
            >
              <CheckIcon className="mr-2 size-4" />
              {irreversible
                ? t('animalManagement.convertAnimalType.continue', 'Continue')
                : t('animalManagement.convertAnimalType.convert', 'Convert')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={open && pendingConversion !== null}
        onOpenChange={(next) => {
          if (!next) setPendingConversion(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangleIcon className="size-5" />
              {t(
                'animalManagement.convertAnimalType.confirmationTitle',
                'Irreversible Conversion Warning',
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                'animalManagement.convertAnimalType.confirmationSubtitle',
                'This conversion cannot be undone.',
              )}
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="flex flex-col gap-4">
            <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm">
              <p className="mb-3">
                You are about to convert <strong>{animal.nameTag}</strong> from{' '}
                <strong>{animal.animalType}</strong> to{' '}
                <strong>{pendingConversion?.newType}</strong>.
              </p>
              <p className="mb-2">
                {t(
                  'animalManagement.convertAnimalType.confirmationAware',
                  'Are you aware that:',
                )}
              </p>
              <ul className="list-disc space-y-1 pl-5">
                <li>
                  This change <strong>cannot be undone</strong>
                </li>
                <li>
                  {t('animalManagement.convertAnimalType.confirmationRetired', {
                    defaultValue:
                      'The current {fromType} record becomes inactive',
                    fromType: animal.animalType,
                  })}
                </li>
                <li>
                  {t(
                    'animalManagement.convertAnimalType.confirmationAudit',
                    'An audit log entry will be created with timestamp and user',
                  )}
                </li>
              </ul>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={convert.isPending}
              onClick={() => setPendingConversion(null)}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="button"
              className="bg-amber-600 hover:bg-amber-700"
              disabled={convert.isPending}
              onClick={() =>
                pendingConversion && runConversion(pendingConversion)
              }
            >
              {t(
                'animalManagement.convertAnimalType.proceedWithConversion',
                'Proceed with Conversion',
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
